import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'

import BasePolicyTable from './BasePolicyTable.vue'
import Legend from '@/components/designSystem/Legend/Legend.vue'
import DsAttendancePolicyModal from '@/components/designSystem/modals/DSAttendancePolicyModal.vue'
import Network from '@/Network'
import { DSAttendanceStatus } from '@/constants/ds-attendance.constants'
import { useRegistrationPolicyPermissions } from '@/modules/company/Policy/composables/useRegistrationPolicyPermissions'
import { hasApiErrorCode } from '@/utils/apiError'
import { resolveCurrentCheckRequirement, type ScheduleCheckFlags } from '@/utils/registrationCheck'
import type { DSAttendancePolicyData } from '@/types/ds-attendance.types'

// 1. IMPORTANDO AS STORES
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { useCreateScheduleStore, SCHEDULE_LOAD_ERROR } from '@/pinia-store/useCreateScheduleStore'
import { useShiftStore } from '@/pinia-store/useShiftStore'

const REGISTRATION_SECTION_FORBIDDEN =
  'Você não tem permissão para editar regras de registro e auditoria desta política. ' +
  'Peça a um administrador da empresa a permissão “Editar registro e auditoria da política”.'

/** Registros de atividade vivos na escala — alimenta o aviso ① de M4. */

export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleDsAttendancePolicyTab',
  props: {
    scheduleId: {},
    scheduleEndDate: {}
  },
  emits: ['create-policy', 'edit-policy', 'loaded-requirements', 'cancel'],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;

const vigencyLegendItems = [
  { label: 'Em vigência', borderColor: 'border-green-500', bgColor: 'bg-green-100' },
  { label: 'Expirada', borderColor: 'border-red-500', bgColor: 'bg-red-100' },
  { label: 'Futura', borderColor: 'border-blue-500', bgColor: 'bg-blue-100' }
]



const modalKey = ref(0)

const toast = useToast()
const { notifyError, notifyErrorMessage } = useErrorToast()
const search = ref('')

/**
 * Q31 (F2/M4) — a aba resolve a permissão uma vez e repassa ao formulário, para não
 * repetir a consulta a cada modal aberto.
 */
const { canEditRegistrationSection } = useRegistrationPolicyPermissions()

/** Mensagem literal do cadeado, reusada quando o backend rejeita o payload (§1.6). */
const openActivityRecords = ref<number | null>(null)

const dsAttendanceLegendItems = [
  { label: 'Em vigência', borderColor: 'border-green-500', bgColor: 'bg-green-100' },
  { label: 'Vencida', borderColor: 'border-red-500', bgColor: 'bg-red-100' },
  { label: 'Futura', borderColor: 'border-blue-500', bgColor: 'bg-blue-100' }
]

const isModalOpen = ref(false)
const selectedPolicy = ref<DSAttendancePolicyData | null>(null)

// 2. INSTANCIANDO AS STORES
const policyStore = useDsAttendanceStore()
const scheduleStore = useCreateScheduleStore()
const shiftStore = useShiftStore()

// 3. EXTRAINDO O ESTADO REATIVO
const { policies, isLoadingPolicies: isLoading, loadingSwitches } = storeToRefs(policyStore)
const { listShifts } = storeToRefs(shiftStore)
const schedule = ref<{ endDate?: string | null }>({})
const isScheduleLoaded = ref(false)

/**
 * M7 (§3/§4) — as flags legadas da escala. Elas não têm tela própria: servem para que a
 * PRIMEIRA política da escala nasça herdando o que ela exige hoje, e como fonte do que
 * "hoje é exigido" enquanto a escala não tiver política vigente. `null` enquanto a escala
 * não carregou: o formulário então cai no default do produto e nenhum aviso é inventado.
 */
const scheduleCheckFlags = ref<ScheduleCheckFlags | null>(null)

/**
 * `política ?? escala`, a mesma precedência do backend: a política VIGENTE responde em
 * bloco; sem ela, respondem as flags. É a linha de base do aviso de desligamento.
 */
const currentCheckRequirement = computed(() =>
  resolveCurrentCheckRequirement(policies.value, scheduleCheckFlags.value)
)

const availableShifts = computed(() =>
  listShifts.value.map((s) => ({ title: s.name, value: s.id }))
)

// 4. LÓGICA DE APRESENTAÇÃO
const filteredPolicies = computed(() => {
  if (!search.value) return policies.value
  return policies.value.filter((p) => p.name.toLowerCase().includes(search.value.toLowerCase()))
})

const handleCreate = () => {
  prepareForm()
  selectedPolicy.value = null
  modalKey.value++
  isModalOpen.value = true
  emit('create-policy')
}

const handleCloseModal = () => {
  isModalOpen.value = false
  selectedPolicy.value = null
  policyStore.resetPolicies()
  fetchPolicies()
}

const handleEditPolicy = async (policy: DSAttendancePolicyData) => {
  if (!policy.id) return
  const policyId = Number(policy.id)

  try {
    const data = await policyStore.fetchPolicyById(policyId, props.scheduleId)
    selectedPolicy.value = data as unknown as DSAttendancePolicyData
    prepareForm()
    modalKey.value++
    isModalOpen.value = true
    emit('edit-policy', policy)
  } catch (err: unknown) {
    notifyError(err, 'Não foi possível carregar os detalhes da política de registro de plantão.')
  }
}

const onSavePolicy = async (data: DSAttendancePolicyData) => {
  try {
    await policyStore.savePolicy(data, props.scheduleId)
    toast.success('Informações salvas com sucesso.')
    isModalOpen.value = false
    await fetchPolicies()
  } catch (err: unknown) {
    // O backend rejeita as seções de registro/auditoria de quem não tem a Q31 (§1.6).
    // Sem este ramo, o 403 viraria "erro ao salvar" genérico e o usuário não saberia
    // que o problema é permissão — nem qual pedir.
    if (hasApiErrorCode(err, 'ACTIVITY_POLICY_SECTION_FORBIDDEN')) {
      // C3 — é este 403 (e só ele) que prova que o usuário não pode editar a seção
      // "Auditoria": a permissão da Q31 não a governa, então até aqui ela fica aberta.
      policyStore.markRegistrationSectionForbidden()
      notifyErrorMessage(REGISTRATION_SECTION_FORBIDDEN)
      return
    }
    notifyError(err, 'Não foi possível salvar a política de registro de plantão.')
  }
}

/**
 * Contagem dos registros de atividade vivos da escala, buscada só quando o modal vai
 * abrir — é o único consumidor do número, e a lista de políticas não paga por ele.
 * Tolerante ao backend antigo: `getCountsByKind` devolve `null` enquanto a rota não
 * existir, e o aviso ① apenas não aparece — a aba nunca quebra por causa dele.
 */
const fetchOpenActivityRecords = async () => {
  if (!props.scheduleId) return

  try {
    const counts = await Network.dsAttendance.getCountsByKind({
      scheduleIds: [props.scheduleId],
      status: [
        DSAttendanceStatus.PENDING_SUBMISSION,
        DSAttendanceStatus.PENDING_AUDIT,
        DSAttendanceStatus.AUDIT_IN_PROGRESS
      ],
      page: 1,
      limit: 1
    })
    openActivityRecords.value = counts?.atividade ?? null
  } catch (err: unknown) {
    console.debug('[DsAttendancePolicy] contagem de registros de atividade indisponível', err)
    openActivityRecords.value = null
  }
}

/**
 * Tudo o que só o FORMULÁRIO precisa, buscado quando o modal vai abrir. Inclui a
 * feature detection do backend da F2 (C3): sem ela o cadeado da Q31 não existe, e é
 * ela que impede o deploy do web de travar sozinho a seção "Auditoria" já em PROD.
 * A lista de políticas não paga por nenhuma das duas chamadas.
 */
const prepareForm = () => {
  void policyStore.fetchRegistrationSettings(props.scheduleId)
  void fetchOpenActivityRecords()
}

const fetchPolicies = async () => {
  if (!props.scheduleId) return

  try {
    await policyStore.fetchPolicies(props.scheduleId)
  } catch (err: unknown) {
    // A action engolia a falha (só `console.error`), então este catch nunca disparava e
    // a aba ficava vazia como se a escala não tivesse política. Agora ela relança.
    notifyError(err, 'Não foi possível carregar as políticas de registro de plantão.')
  }
}

const handleTogglePolicyStatus = async (id: number | string, enabled: boolean) => {
  try {
    await policyStore.togglePolicyStatus(Number(id), enabled, props.scheduleId)
    toast.success('Status da política de registro de plantão atualizado com sucesso.')
  } catch (err: unknown) {
    notifyError(err, 'Não foi possível alterar o status da política de registro de plantão.')
  }
}

const loadRequirements = async () => {
  try {
    const scheduleData = await scheduleStore.fetchScheduleById(props.scheduleId)

    if (scheduleData) {
      schedule.value = scheduleData
      scheduleCheckFlags.value = {
        ourTeam: Boolean(scheduleData.requiredCheckOurTeam),
        notOurTeam: Boolean(scheduleData.requiredCheckNotOurTeam)
      }
      emit('loaded-requirements', scheduleData.requiresDoctorPaymentFormation)
      isScheduleLoaded.value = true
    }
  } catch (err: unknown) {
    // Era `catch { console.error }`: sem toast nem estado, a aba ficava só sem os
    // requisitos e o usuário não sabia que a carga havia falhado.
    console.error('Erro ao carregar requisitos:', err)
    notifyError(err, SCHEDULE_LOAD_ERROR)
  }
}

watch(
  () => props.scheduleId,
  (newId) => {
    if (newId) {
      policyStore.resetPolicies()
      fetchPolicies()
      shiftStore.fetchByScheduleId(newId)
    }
  }
)

onMounted(() => {
  fetchPolicies()
  loadRequirements()
  shiftStore.fetchByScheduleId(props.scheduleId)
})

__expose({ handleSave: fetchPolicies })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(BasePolicyTable, {
      search: search.value,
      "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
      policies: filteredPolicies.value,
      "is-loading": _unref(isLoading),
      "loading-switches": _unref(loadingSwitches),
      "can-create": isScheduleLoaded.value,
      "can-edit": isScheduleLoaded.value,
      "legend-items": dsAttendanceLegendItems,
      onCreate: handleCreate,
      onEdit: handleEditPolicy,
      onToggleStatus: handleTogglePolicyStatus
    }, {
      legend: _withCtx(() => [
        _createVNode(Legend, {
          items: vigencyLegendItems,
          class: "text-sm text-gray-600"
        })
      ]),
      _: 1
    }, 8, ["search", "policies", "is-loading", "loading-switches", "can-create", "can-edit"]),
    (_openBlock(), _createBlock(DsAttendancePolicyModal, {
      key: modalKey.value,
      "is-open": isModalOpen.value,
      "initial-data": selectedPolicy.value,
      "available-shifts": availableShifts.value,
      "schedule-end-date": props.scheduleEndDate ?? schedule.value.endDate,
      "can-edit-registration-section": _unref(canEditRegistrationSection),
      "open-activity-records": openActivityRecords.value,
      "schedule-id": props.scheduleId,
      "inherited-check-flags": scheduleCheckFlags.value,
      "current-check-requirement": currentCheckRequirement.value,
      onClose: handleCloseModal,
      onSave: onSavePolicy
    }, null, 8, ["is-open", "initial-data", "available-shifts", "schedule-end-date", "can-edit-registration-section", "open-activity-records", "schedule-id", "inherited-check-flags", "current-check-requirement"]))
  ]))
}
}

})